// Maps a backend tx `current_event` to a short, user-facing phase label shown
// while a document is processing. Covers every event name the backend emits
// (pf/txlog/tx_event_names.py) so the label never blanks out mid-processing —
// unknown events return null and callers keep the previous phase. Keep in sync
// with tx_event_names.py.
const PHASE_LABELS: Record<string, string> = {
  // Ingestion
  OCR_STARTED: "Extracting text",
  OCR_CLOUD_PROCESSING_STARTED: "Extracting text",
  OCR_CLOUD_PROCESSING_PROGRESS: "Extracting text",
  OCR_CLOUD_PROCESSING_DOWNLOAD: "Extracting text",
  OCR_CLOUD_PROCESSING_FINISHED: "Extracting text",
  OCR_FINISHED: "Extracting text",
  VSTORE_INDEXING_STARTED: "Indexing",
  VSTORE_INDEXING_ENDED: "Indexing",
  SUMMARIZATION_STARTED_EVENT: "Summarizing",
  SUMMARIZATION_ENDED_EVENT: "Summarizing",
  INGESTION_FINISHED: "Finalizing",
};

export const getPhaseLabel = (currentEvent: string | null | undefined): string | null => {
  if (!currentEvent) return null;
  return PHASE_LABELS[currentEvent] ?? null;
};

// Forward-only ordering of phases. The ingestion pipeline runs vector indexing
// and summarization *in parallel* (a Celery group), so the tx's single
// current_event legitimately ping-pongs between them — Indexing and Summarizing
// share a rank so neither replaces the other once shown. Higher rank = later
// stage; the UI only ever advances.
const PHASE_ORDER: Record<string, number> = {
  OCR_STARTED: 1,
  OCR_CLOUD_PROCESSING_STARTED: 1,
  OCR_CLOUD_PROCESSING_PROGRESS: 1,
  OCR_CLOUD_PROCESSING_DOWNLOAD: 1,
  OCR_CLOUD_PROCESSING_FINISHED: 1,
  OCR_FINISHED: 1,
  VSTORE_INDEXING_STARTED: 2,
  VSTORE_INDEXING_ENDED: 2,
  SUMMARIZATION_STARTED_EVENT: 2,
  SUMMARIZATION_ENDED_EVENT: 2,
  INGESTION_FINISHED: 3,
};

// Rank of a phase event. No event → 0 (the "Uploading" baseline). Unknown → -1
// so it never advances the phase.
const getPhaseOrder = (currentEvent: string | null | undefined): number => {
  if (!currentEvent) return 0;
  return PHASE_ORDER[currentEvent] ?? -1;
};

// Pick the phase event to display next: keep whichever is later in the pipeline
// and never switch between equally-ranked parallel phases. Returns the event to
// store on the row/item. Used by both the upload modal and the drive list so a
// file's phase advances identically (and monotonically) in both views.
export const advancePhaseEvent = (
  prevEvent: string | null | undefined,
  incomingEvent: string | null | undefined,
): string | undefined => {
  if (!getPhaseLabel(incomingEvent)) return prevEvent ?? undefined;
  return getPhaseOrder(incomingEvent) > getPhaseOrder(prevEvent)
    ? (incomingEvent ?? undefined)
    : (prevEvent ?? undefined);
};

// Shown while a file is processing but no phase event has arrived yet (the early
// 0–10% upload window). Used by both the upload modal and the drive-list row so
// the two views read identically for the same file.
export const PHASE_FALLBACK_LABEL = "Uploading";

type UploadRowStatus = "pending" | "started" | "uploading" | "completed" | "error";

export const getFilePhaseLabel = (status: UploadRowStatus, currentEvent: string | null | undefined): string => {
  if (status === "completed") return "Completed";
  if (status === "error") return "Failed";
  if (status === "pending") return "Waiting";
  return getPhaseLabel(currentEvent) ?? PHASE_FALLBACK_LABEL;
};

// Fixed width reserved for the phase text so the label never shifts the layout as
// it changes ("Uploading" → "Extracting text" → "Summarizing" …). Wide
// enough for the longest label. Same value in the modal and the drive list.
export const PHASE_LABEL_WIDTH_CLASS = "w-28";
